import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointLoyaltyCustomersDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import Button from '../../../../../../components/base/button/button';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import StatusPill from '../../components/status-pill';
import {paginateMockItems} from '../../mock-search';
import {mockCustomers} from '../../mock-data';
import SetContactDialog from './set-contact-dialog';
import PointsReportDialog from './points-report-dialog';

const dataGridSaveConfig = {name: 'BizPointLoyaltyCustomersDataGrid', version: 2};
const col = BizPointLoyaltyCustomersDataGridColumnNames;

const searchOptions = {
    keywordFields: ['customerNo', 'name', 'email', 'phone'],
    sortFieldMap: {
        [col.customerNo]: 'customerNo',
        [col.name]: 'name',
        [col.confirmedBiz]: 'confirmedBiz',
        [col.frozen]: 'frozen',
    },
};

const LoyaltyCustomersView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.customers'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canExportLoyaltyCustomers = packages.bizPoint.exportLoyaltyCustomers;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-loyalty-customers');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [sourceItems, setSourceItems] = useState(mockCustomers);
    const [data, setData] = useState([]);
    const [contactCustomer, setContactCustomer] = useState(null);
    const [reportCustomer, setReportCustomer] = useState(null);

    /**
     * Configures notebook tags and behavioral analytics for the customers page.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointLoyaltyCustomers);
        configureBehavioralTool((tags) => tags.bizPointLoyaltyCustomers);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('customerNo'), name: col.customerNo, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('name'), name: col.name, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {
            title: t('contact'),
            name: col.contact,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            width: 240,
        },
        {
            title: t('confirmedBiz'),
            name: col.confirmedBiz,
            type: DataGridColumnTypes.number,
            alignment: DataGridColumnAlignments.right,
        },
        {title: t('frozen'), name: col.frozen, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {
            title: t('walletStatus'),
            name: col.walletStatus,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: t('actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 200,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads a page of customers from mock data with keyword filter and sort.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(sourceItems, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted, sourceItems]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(sourceItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.contact]: (item) => {
                if (!item.email && !item.phone) {
                    return t('noContactSet');
                }
                return [item.email, item.phone].filter(Boolean).join(' / ');
            },
            [col.walletStatus]: (item) => (item.pending > 0 ? t('unclaimed') : t('claimed')),
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, sourceItems, t]);

    /**
     * Applies saved contact fields to the local customer list.
     */
    const handleContactSaved = useCallback(({customerNo, email, phone}) => {
        setSourceItems((prev) =>
            prev.map((c) =>
                c.customerNo === customerNo
                    ? {...c, email, phone}
                    : c,
            ),
        );
        setContactCustomer(null);
        searchData.performSearch();
    }, [searchData]);

    const rows = useMemo(() => data.map((row) => {
        const hasContact = Boolean(row.email || row.phone);
        return {
            key: row.customerNo,
            data: row,
            cells: {
                [col.customerNo]: row.customerNo,
                [col.name]: row.name,
                [col.contact]: hasContact
                    ? (
                        <Stack spacing={0.25}>
                            {row.email && <Typography variant="body2">{row.email}</Typography>}
                            {row.phone && (
                                <Typography variant="body2" color="text.secondary">
                                    {row.phone}
                                </Typography>
                            )}
                        </Stack>
                    )
                    : <StatusPill flag label={t('noContactSet')}/>,
                [col.confirmedBiz]: row.confirmedBiz?.toLocaleString?.() ?? row.confirmedBiz,
                [col.frozen]: row.frozen,
                [col.walletStatus]: row.pending > 0
                    ? <StatusPill flag status="unclaimed" label={t('unclaimed')}/>
                    : <StatusPill status="claimed" label={t('claimed')}/>,
                [col.actions]:
                    <Stack direction="row" spacing={1}>
                        {packages.bizPoint.loyaltyCustomersManage && (
                            <Button size="small" variant="outlined" onClick={() => setContactCustomer(row)}>
                                {t('setContact')}
                            </Button>
                        )}
                        <Button size="small" variant="outlined" onClick={() => setReportCustomer(row)}>
                            {t('report')}
                        </Button>
                    </Stack>,
            },
        };
    }), [packages.bizPoint.loyaltyCustomersManage, data, t]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportLoyaltyCustomers ? getExportInfo : undefined}
            />

            <SetContactDialog
                open={Boolean(contactCustomer)}
                customer={contactCustomer}
                onClose={() => setContactCustomer(null)}
                onSaved={handleContactSaved}
            />

            <PointsReportDialog
                open={Boolean(reportCustomer)}
                customer={reportCustomer}
                onClose={() => setReportCustomer(null)}
            />
        </Box>
    );
};

export default LoyaltyCustomersView;
